import { apyToAprPercent } from '../../../utils/parsing'
import type { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Vesper `va*` pools — realized supply rate from Vesper's own API.
 *
 * Vesper (vesper.finance) runs strategy-backed single-asset pools: deposits
 * are routed through weighted strategies (Morpho / Aave / Compound / Yearn
 * lending legs, some leveraged borrow loops) and the share price
 * (`pricePerShare()`) accrues what they earn net of the universal fee.
 *
 * **Primary: the per-chain Vesper API's `actualRates`** — realized
 * share-price growth over trailing windows (keys are day counts), simple-
 * annualized, in percent, i.e. already this package's nominal-APR
 * convention. The 30-day window is used (14-day fallback) because it is
 * what a depositor actually earned, and it CAN BE NEGATIVE — several
 * leveraged-borrow pools (vaCBETH, vaWBTC) ran underwater for weeks with
 * `interestEarned` negative on-chain. A negative here is a measurement,
 * not an artifact; never clamp it (the HLP / Strata-junior rule).
 *
 * The API is per-chain by HOST, not by query param — `?chainId=` is
 * silently ignored and every host answers only its own book. The Avalanche
 * and Polygon hosts are dead (deployments retired); do not re-add them
 * without checking the pools still exist.
 *
 * **`actualRates` is NOT what vesper.finance's own pool page renders.**
 * Their UI shows `earningRates` (the 14-day key), and on chain-1 vaETH the
 * two disagree by two orders of magnitude — checked 2026-09-09, when the
 * same API response carried `actualRates {7: -0.19, 14: 0.39, 30: 0.47}`
 * beside `earningRates {7: 150.44, 14: 89.54, 30: 27.07}` and their page
 * read 89.51 %. **Ours is the correct one**, adjudicated against archival
 * `pricePerShare()`: 1.055207974721 → 1.056704141265 over 89.7 days, i.e.
 * +0.1418 % in three months (0.58 % simple APR), and literally unchanged
 * over the preceding 24 h. `earningRates` is strategy-reported earnings
 * over a collapsing denominator — it ramped 7.5 % → 89.65 % in eight days
 * while the pool shrank 1,538 → 1,169 ETH on a single redemption. The two
 * fields agree on 10 of the 11 curated rows, so the divergence hides. Do
 * not "fix" our row to match theirs; see VESPER.md.
 *
 * **Fallback: DefiLlama** (`project=vesper`, per-pool UUIDs pinned below).
 * Deliberately second: Llama's `apyBase` for these pools tracks that same
 * forward-looking `earningRates`, which diverges wildly from the realized
 * figure on some pools (vaETH: 9.11 % on Llama vs 0.8 % realized when this
 * was written; **89.65 % vs 0.47 %, a 190x gap, by 2026-09-09**) — the
 * realized number is the honest one, so the fallback only fills pools the
 * API leg failed entirely.
 *
 * **KNOWN HAZARD, unfixed:** that fallback is whole-leg. If the chain-1
 * host times out below, all six chain-1 pools switch to Llama at once —
 * and only vaETH visibly moves, so it surfaces as a lone ~9 % spike in an
 * otherwise flat ~0.5 % recorded series (two such spikes between
 * 2026-08-27 and 2026-09-09 match Llama's 8.10–9.50 % readings for those
 * exact days). Measured host latency is 0.12 s on all three chains, so a
 * failure is a transport blip, not slowness: the fix is a retry of the
 * host leg plus last-known-good, NOT removing a fallback that is within
 * 1.2x on 8 of the 11 pools.
 *
 * Keys are per-(chain, pool address): the deployments are independent
 * books with independent strategies, so one symbol has a different rate on
 * every chain. No token-list assetGroup keys are emitted — no va* share is
 * lending collateral anywhere we serve, so there is no lending join to
 * feed (revisit if one is ever listed as collateral).
 */

/** Registry `yieldKey` for a pool. */
export const vesperYieldKey = (chainId: string, poolAddress: string): string =>
  `VESPER::${chainId}::${poolAddress.toLowerCase()}`

/** Per-chain API hosts (see the host note above). */
const VESPER_API_HOSTS: { [chainId: string]: string } = {
  '1': 'https://api.vesper.finance',
  '8453': 'https://api-base.vesper.finance',
  '10': 'https://api-optimism.vesper.finance',
}

/**
 * DefiLlama pool UUIDs (`project=vesper`), keyed by our yield key. Only the
 * curated pools — the fallback exists to fill an API outage, not to widen
 * coverage. APYs in the comments are the values at pinning time
 * (2026-08-27) — sanity anchors, not assertions.
 */
const DEFILLAMA_POOLS: { [yieldKey: string]: string } = {
  // Ethereum
  [vesperYieldKey('1', '0xca7c607c590ad16007ccbbba9d26f4df656a36c2')]:
    '4208b678-e7cc-4060-9c74-88554e8ca81e', // vamsETH 0 %
  [vesperYieldKey('1', '0x4c73f025a1947ec770327b9956fc61f535f72c22')]:
    '3987099a-9165-47a3-a92f-df14e8d60e45', // vamsUSD 0.21 %
  [vesperYieldKey('1', '0x4dbe3f01abe271d3e65432c74851625a8c30aa7b')]:
    '9b473092-6f2d-4fe6-af5a-f746b77ef5a0', // vaSTETH 1.41 %
  [vesperYieldKey('1', '0xd1c117319b3595fbc39b471ab1fd485629eb05f2')]:
    '359dd5cd-67a6-4f6a-83db-1edb301637e7', // vaETH — 9.11 %, then 89.65 % on
  // 2026-09-09 against a realized 0.47 %. THE WORST ROW IN THIS TABLE: never
  // treat a Llama value for this pool as plausible. See the hazard note above.
  [vesperYieldKey('1', '0xa8b607aa09b6a2e306f93e74c282fb13f6a80452')]:
    'eb342dc8-8d50-4300-8e92-c8d88e026c94', // vaUSDC 3.16 %
  [vesperYieldKey('1', '0x0538c8bac84e95a9df8ac10aad17dbe81b9e36ee')]:
    'd07783c3-bd68-4e38-927f-762fcb349dfc', // vaDAI 0.03 %
  // Base
  [vesperYieldKey('8453', '0x913ece180df83a2b81a4976f83ca88543a0c51b8')]:
    'e2b9cdfc-e605-4e5b-9feb-512729addac7', // vamsETH 0.06 %
  [vesperYieldKey('8453', '0x82562507429876486b60af4f32390ef0947b3d13')]:
    'aba92dbe-f256-4be3-8fd5-21d6b51bd69c', // vaETH 0.12 %
  // Optimism
  [vesperYieldKey('10', '0xdd63ae655b388cd782681b7821be37fdb6d0e78d')]:
    'fec05017-ea81-417b-ae8b-20a86e4b23a7', // vawstETH 0 %
  [vesperYieldKey('10', '0x539505dde2b9771debe0898a84441c5e7fdf6bc0')]:
    '4d94e177-4162-4b30-b72b-f7a77ed0573a', // vaUSDC 0.03 %
  [vesperYieldKey('10', '0xccf3d1acf799bae67f6e354d685295557cf64761')]:
    '43e149fc-a0d8-4680-8bb5-8ba12f09e94a', // vaETH 0.6 %
}

interface VesperApiPool {
  address?: string
  actualRates?: { [windowDays: string]: number }
}

/** One chain's pool list from Vesper's API → `yieldKey → aprPercent`. */
const fetchChainRates = async (
  chainId: string,
  host: string,
): Promise<Record<string, number>> => {
  const res = await fetch(`${host}/pools?stages=prod`, {
    headers: { accept: 'application/json' },
    signal: AbortSignal.timeout(8000),
  })
  if (!res.ok) throw new Error(`Vesper HTTP ${res.status} (${host})`)
  const pools = (await res.json()) as VesperApiPool[]
  if (!Array.isArray(pools)) throw new Error(`Vesper: non-array (${host})`)

  const out: Record<string, number> = {}
  for (const p of pools) {
    if (!p?.address) continue
    const rate = p.actualRates?.['30'] ?? p.actualRates?.['14']
    if (typeof rate !== 'number' || !Number.isFinite(rate)) continue
    out[vesperYieldKey(chainId, p.address)] = rate
  }
  return out
}

export const vesperFetcher: YieldFetcher = {
  label: 'VESPER',
  fetch: async () => {
    // Per-chain and independent — a dead host (the Avalanche/Polygon fate)
    // must not take the other chains' rates down with it.
    const legs = await Promise.allSettled(
      Object.entries(VESPER_API_HOSTS).map(([chainId, host]) =>
        fetchChainRates(chainId, host),
      ),
    )
    const out: Record<string, number> = {}
    for (const leg of legs) {
      if (leg.status === 'fulfilled') Object.assign(out, leg.value)
    }

    // DefiLlama fills only the curated pools the API leg left empty.
    const missing = Object.entries(DEFILLAMA_POOLS).filter(
      ([key]) => out[key] === undefined,
    )
    if (missing.length > 0) {
      const fills = await Promise.allSettled(
        missing.map(async ([key, poolId]) => {
          const apy = await fetchDefiLlamaApy(poolId)
          return [key, apyToAprPercent(apy)] as const
        }),
      )
      for (const f of fills) {
        if (f.status === 'fulfilled') out[f.value[0]] = f.value[1]
      }
    }

    if (Object.keys(out).length === 0) {
      throw new Error('Vesper: no rates from any API host or DefiLlama')
    }
    return out
  },
}
